import { useEffect, useMemo } from 'react';
import { Model } from '@shared/types';
import { ModelConfig } from '@/types/misc';
import { IMAGE_TO_CAD_MODELS, PARAMETRIC_MODELS } from '@/lib/utils';

// Image-to-CAD generation from an uploaded image is exclusive to the
// image-capable tiers. Attaching an image locks the model picker to these and
// defaults to Magneto 0.1; the other (text-seeded) tiers grey out until the
// image is removed.
export const IMAGE_TIERS: Model[] = [
  'trellis1',
  'trellis2',
  'trellis2alias',
  'magneto4b',
  'pixal3d',
  'meshy5',
  'meshy6',
  'meshy7',
  'hunyuan_v2',
  'hunyuan_v2_mini',
  'hunyuan_v3',
  'hunyuan',
  'hunyuan_rapid',
  'tripo',
  'tripo_h31',
  'tripo_p1',
  'triposr',
];
const DEFAULT_IMAGE_TIER: Model = 'meshy7';

export const isImageTier = (id: Model): boolean => IMAGE_TIERS.includes(id);

/**
 * Grey out (disable) the non-image tiers when an image is attached. Both image
 * tiers stay selectable for any image count. Pure — never mutates the input.
 */
export function applyImageTierLock(
  models: ModelConfig[],
  imagesAttached: boolean,
): ModelConfig[] {
  if (!imagesAttached) return models;
  return models.map((m) => (isImageTier(m.id) ? m : { ...m, disabled: true }));
}

/**
 * Owns the model-dropdown list for the chat box: picks the base tier list from
 * `type`, and — when an image is attached in image-to-CAD mode — restricts it to
 * the image tiers (greying out the rest) and auto-selects Magneto 0.1.
 *
 * @returns `models` to feed the dropdown (non-image tiers carry `disabled` while
 *   an image is attached) and the `imagesAttached` flag.
 */
export function useImageModelLock(
  type: 'parametric' | 'imagetocad',
  images: unknown[],
  model: Model,
  setModel: (model: Model) => void,
): { models: ModelConfig[]; imagesAttached: boolean } {
  const imagesAttached = type === 'imagetocad' && images.length > 0;

  // Adding any image(s) locks the picker to the image tiers and defaults to
  // Magneto 0.1. All image tiers stay selectable regardless of image count.
  useEffect(() => {
    if (imagesAttached && !isImageTier(model)) {
      setModel(DEFAULT_IMAGE_TIER);
    }
  }, [imagesAttached, model, setModel]);

  const models = useMemo<ModelConfig[]>(() => {
    const base =
      type === 'imagetocad' ? IMAGE_TO_CAD_MODELS : PARAMETRIC_MODELS;
    return applyImageTierLock(base, imagesAttached);
  }, [type, imagesAttached]);

  return { models, imagesAttached };
}
